import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Model from "@tripian/model";
import useTimeline from "../../hooks/useTimeline";
import useTranslate from "../../hooks/useTranslate";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import Navbar from "./components/Navbar";
import TripPlannerForm from "./components/trip_planner/TripPlannerForm";
import Footer from "./components/Footer";
import AppNav from "../../App/AppNav/AppNav";

const EditTimelineTrip: React.FC = () => {
  const { tripHash, segmentIndex } = useParams<{ tripHash: string; segmentIndex?: string }>();
  const navigate = useNavigate();
  const { t } = useTranslate();
  const { getLocalData } = useLocalStorage<"light" | "dark">("theme");
  const theme = getLocalData();

  const { timelineTrip, timelineFetch, loadingTimeline } = useTimeline();
  const isValaiLanding = window.tconfig.LANDING_PAGE === "ValaiLanding";

  // Determine if this is create mode (no segmentIndex in URL)
  const isCreateMode = segmentIndex === undefined;

  const [initialData, setInitialData] = useState<{
    tripHash: string;
    segment?: Model.TimelineTripProfileSegment;
    segmentIndex?: number;
    existingSegmentCount?: number;
  } | null>(null);

  // Fetch timeline trip data on mount
  useEffect(() => {
    if (tripHash) {
      timelineFetch(tripHash);
    }
  }, [tripHash, timelineFetch]);

  // Extract segment data when trip is loaded
  useEffect(() => {
    if (timelineTrip && tripHash) {
      const segments = timelineTrip.tripProfile?.segments || [];
      const existingSegmentCount = segments.length;

      if (isCreateMode) {
        // Create mode - no segment data, just tripHash and existing count
        setInitialData({
          tripHash,
          segment: undefined,
          segmentIndex: undefined,
          existingSegmentCount,
        });
      } else {
        // Edit mode - load segment data
        if (segments.length === 0) return;

        const targetIndex = parseInt(segmentIndex!, 10);
        const targetSegment = segments[targetIndex];

        if (targetSegment && targetIndex !== -1) {
          setInitialData({
            tripHash,
            segment: targetSegment,
            segmentIndex: targetIndex,
            existingSegmentCount,
          });
        }
      }
    }
  }, [timelineTrip, tripHash, segmentIndex, isCreateMode]);

  if (loadingTimeline) {
    return (
      <div className="min-h-screen font-geist text-[#0B1215] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-color mx-auto mb-4"></div>
          <p className="text-gray-600">Loading trip data...</p>
        </div>
      </div>
    );
  }

  if (!initialData && !loadingTimeline) {
    return (
      <div className="min-h-screen font-geist text-[#0B1215] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Trip not found or no available segment.</p>
          <button
            className="px-4 py-2 bg-primary-color text-white rounded-lg"
            onClick={() => navigate(-1)}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen font-geist text-[#0B1215]">
      {isValaiLanding ? (
          <Navbar
              theme={theme}
              t={t}
          />
      ) : (
          <AppNav header="" />
      )}
      <main className="container mx-auto py-12">
        <TripPlannerForm
          editMode
          editTripHash={initialData?.tripHash}
          editSegment={initialData?.segment}
          editSegmentIndex={initialData?.segmentIndex}
          existingSegmentCount={initialData?.existingSegmentCount}
        />
      </main>
      {isValaiLanding && <Footer t={t} />}
    </div>
  );
};

export default EditTimelineTrip;